import React, { Component, createRef } from 'react';

// CSS
import '../Subscriptions/Subscription.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Confirm } from '../../components/UI/Overlays';
import { PageLoading, Alert, Chip } from '../../components/UI/Feedback';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

// Properties
const properties = require('../../properties.json');

// kind is the standard set's own vocabulary for a state word: ok | wait | off | bad
const STATUS_META = {
    NONE: { kind: 'off', label: 'لم يتم الإضافة' },
    PENDING: { kind: 'wait', label: 'بإنتظار الربط' },
    VERIFIED: { kind: 'ok', label: 'تم التفعيل' },
    FAILED: { kind: 'bad', label: 'فشل الربط' },
};

class Domain extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
        this.pollTimer = null;
    }

    state = {
        domainInput: '',
        customDomain: null,
        customDomainStatus: 'NONE',
        saving: false,
        loading: true,

        // the teacher asked to unlink the domain, held while the confirm is up
        doomed: false,
        removing: false,
    }

    componentDidMount() {
        document.title = properties.site_name + " | الدومين الخاص";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        this.loadMe();
    }

    componentWillUnmount() {
        clearTimeout(this.pollTimer);
    }

    loadMe = () => {
        apiFetch("/teachers/me")
            .then(me => {
                this.setState({
                    customDomain: me.customDomain,
                    customDomainStatus: me.customDomainStatus || 'NONE',
                    domainInput: me.customDomain || '',
                    loading: false,
                });
                // Keep checking while the DNS/certificate provisioning script hasn't caught up yet.
                if (me.customDomainStatus === 'PENDING') {
                    this.pollTimer = setTimeout(this.loadMe, 10000);
                }
            })
            .catch(error => {
                this.setState({ loading: false });
                console.error('Error:', error);
            });
    }

    saveDomain = () => {
        const domain = (this.state.domainInput || '').trim().toLowerCase();
        if (!domain) return;
        this.setState({ saving: true });
        apiFetch("/teachers/me/custom-domain", { method: "PUT", body: { domain } })
            .then(me => {
                this.setState({
                    customDomain: me.customDomain,
                    customDomainStatus: me.customDomainStatus,
                    saving: false,
                });
                this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'جاري التحقق من الدومين، تابع الحالة تحت' });
                clearTimeout(this.pollTimer);
                this.pollTimer = setTimeout(this.loadMe, 10000);
            })
            .catch(error => {
                this.setState({ saving: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    // The browser's own confirm box is gone; the question is asked in the
    // product's own confirm card instead. The call behind it is unchanged.
    removeDomain = () => {
        this.setState({ removing: true });
        apiFetch("/teachers/me/custom-domain", { method: "DELETE" })
            .then(me => {
                clearTimeout(this.pollTimer);
                this.setState({
                    customDomain: me.customDomain,
                    customDomainStatus: me.customDomainStatus,
                    domainInput: '',
                    doomed: false,
                    removing: false,
                });
                this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم إلغاء ربط الدومين' });
            })
            .catch(error => {
                this.setState({ doomed: false, removing: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    // A DNS record is copied character by character into someone else's
    // control panel, so it is handed over whole rather than retyped.
    copy = (value) => {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(value)
            .then(() => this.toast.current.show({ severity: 'info', summary: 'تم النسخ', detail: value }))
            .catch(() => {});
    }

    render() {
        const { domainInput, customDomain, customDomainStatus, saving, loading, doomed, removing } = this.state;
        const statusMeta = STATUS_META[customDomainStatus] || STATUS_META.NONE;
        const serverIp = properties.manasa_server_ip || '';

        // the two records the teacher has to create, built from the domain
        // they actually saved
        const records = customDomain
            ? [
                { host: customDomain, note: 'الموقع اللي طلابك بيدخلوا عليه' },
                { host: `admin.${customDomain}`, note: 'لوحة تحكمك' },
            ]
            : [];

        return (
            <>
                {/* the save rides the page's own title strip, so it is in
                    sight from the first frame rather than down beside a field */}
                <Header
                    pageName="الدومين الخاص"
                    action={{
                        label: saving ? "جاري الحفظ..." : "حفظ",
                        icon: saving ? "pi pi-spin pi-spinner" : "pi pi-check",
                        disabled: saving || !domainInput,
                        onClick: this.saveDomain,
                    }}
                />
                <Toast ref={this.toast}></Toast>

                <Confirm
                    visible={doomed}
                    onHide={() => this.setState({ doomed: false })}
                    name={customDomain || ''}
                    note="الموقع هيرجع يشتغل على العنوان الأصلي، وطلابك مش هيوصلوا من الدومين ده تاني."
                    confirmLabel="أيوه، ألغي الربط"
                    busy={removing}
                    onConfirm={this.removeDomain}
                />

                <div className="content">
                    <div className="container">
                        <div className="card">
                            <Card>
                                {/* the page keeps its shape while the first
                                    request is out, instead of rendering nothing */}
                                <PageLoading busy={loading}>
                                    <section className="ui-sec" style={{ "--d": "0ms" }}>
                                        <h3>
                                            <span className="n">١</span>
                                            <span className="t">
                                                دومينك
                                                <small>اكتبه من غير http:// ولا www</small>
                                            </span>
                                        </h3>

                                        <Alert kind="info" title="إزاي هيشتغل؟">
                                            طلابك هيدخلوا على <b>الدومين نفسه</b>، ولوحة تحكمك هتبقى على{' '}
                                            <b>admin.الدومين</b>.
                                        </Alert>

                                        {/* Not a p-inputgroup: that is PrimeReact's
                                            "field with an addon glued to it", and it
                                            restyles whatever button sits in it. The
                                            save is on the page header now, so the
                                            field has the line to itself. */}
                                        <div className="ui-domain-row">
                                            <label className="ui-field" htmlFor="domain-input">
                                                <span className="ui-label">الدومين</span>
                                                <InputText
                                                    id="domain-input"
                                                    value={domainInput}
                                                    placeholder="example.com"
                                                    onChange={(e) => this.setState({ domainInput: e.target.value })}
                                                />
                                            </label>
                                        </div>
                                    </section>

                                    {customDomain ? (
                                        <section className="ui-sec" style={{ "--d": "70ms" }}>
                                            <h3>
                                                <span className="n">٢</span>
                                                <span className="t">
                                                    الحالة
                                                    <small>{customDomain}</small>
                                                </span>
                                                <span className="act">
                                                    <Chip kind={statusMeta.kind}>{statusMeta.label}</Chip>
                                                </span>
                                            </h3>

                                            {customDomainStatus === 'PENDING' ? (
                                                <>
                                                    <Alert kind="warn" title="ناقص خطوة عندك:">
                                                        اضبط الـ DNS عند مزوّد الدومين بالسجلات دي، وبمجرد ما يتظبط
                                                        هيتفعل تلقائيًا خلال دقائق.
                                                    </Alert>

                                                    <div className="ui-dns">
                                                        {records.map((r) => (
                                                            <div className="rec" key={r.host}>
                                                                <span className="kind">A</span>
                                                                <span className="pair">
                                                                    <b>{r.host}</b>
                                                                    <small>{r.note}</small>
                                                                </span>
                                                                <span className="arrow" aria-hidden="true">
                                                                    <i className="pi pi-arrow-left"></i>
                                                                </span>
                                                                {serverIp ? (
                                                                    <button
                                                                        type="button"
                                                                        className="val"
                                                                        onClick={() => this.copy(serverIp)}
                                                                        title="انسخ"
                                                                    >
                                                                        <code>{serverIp}</code>
                                                                        <i className="pi pi-copy"></i>
                                                                    </button>
                                                                ) : (
                                                                    <span className="val is-missing">
                                                                        اتواصل معانا علشان نديك الـ IP
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            ) : null}

                                            {customDomainStatus === 'FAILED' ? (
                                                <Alert kind="bad" title="تعذر تفعيل الدومين:">
                                                    تأكد إن الـ DNS مضبوط صح وجرب تحفظ تاني.
                                                </Alert>
                                            ) : null}

                                            {customDomainStatus === 'VERIFIED' ? (
                                                <Alert kind="ok" title="الدومين شغال:">
                                                    طلابك يقدروا يدخلوا على <b>{customDomain}</b> دلوقتي.
                                                </Alert>
                                            ) : null}

                                            <div className="ui-form-actions">
                                                <button
                                                    type="button"
                                                    className="ui-btn is-ghost"
                                                    onClick={() => this.setState({ doomed: true })}
                                                >
                                                    <i className="pi pi-times"></i>
                                                    <span>إلغاء ربط الدومين</span>
                                                </button>
                                            </div>
                                        </section>
                                    ) : null}
                                </PageLoading>
                            </Card>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default Domain;
